/* ==========================================================================
   Mily Extensions — dragonfly ambience
   --------------------------------------------------------------------------
   Renders a small number of rose-gold dragonflies that drift across a
   section. Deliberately conservative:

     - Under prefers-reduced-motion the shop owner decides: fly anyway, show
       it at rest, or hide it (setting mily_dragonfly_reduced_motion).
     - Still image on low-memory / low-core devices; nothing under Save-Data.
     - Runs only while the host section is on screen and the tab is visible.
     - Every layer is pointer-events: none, so buttons stay clickable.
     - Uses the Web Animations API (compositor-friendly transforms only).
   ========================================================================== */

(function () {
  'use strict';

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  var MOBILE  = window.matchMedia && window.matchMedia('(max-width: 749px)');

  function saveDataOn() {
    var conn = navigator.connection || {};
    return !!conn.saveData;
  }

  function deviceIsWeak() {
    if (typeof navigator.deviceMemory === 'number' && navigator.deviceMemory <= 2) return true;
    if (typeof navigator.hardwareConcurrency === 'number' && navigator.hardwareConcurrency <= 2) return true;
    return false;
  }

  // Lee los ajustes comunes de una capa. Lo usan build() y buildStill().
  function readConfig(host) {
    var cfg = {
      count:     parseInt(host.dataset.count, 10)   || 2,
      size:      parseInt(host.dataset.size, 10)    || 120,
      opacity:   parseFloat(host.dataset.opacity)   || 0.85,
      speed:     parseFloat(host.dataset.speed)     || 1,
      src:       host.dataset.src,
      srcSmall:  host.dataset.srcSmall || host.dataset.src,
      webp:      host.dataset.srcWebp,
      webpSmall: host.dataset.srcWebpSmall || host.dataset.srcWebp,
      ratio:     parseFloat(host.dataset.ratio) || 1.553,
      onMobile:  host.classList.contains('mily-dragonflies--mobile')
    };
    if (MOBILE && MOBILE.matches) {
      if (!cfg.onMobile) return null;
      cfg.count = Math.min(cfg.count, 1);
      cfg.size = Math.round(cfg.size * 0.62);
    }
    return cfg;
  }

  // Crea una libelula (envoltorio + <picture> con WebP y respaldo PNG).
  function createSprite(cfg, index) {
    var wrap = document.createElement('div');
    wrap.className = 'mily-dragonfly';
    wrap.style.width = cfg.size + 'px';
    wrap.setAttribute('aria-hidden', 'true');
    wrap.style.animationDelay = (index * 0.45) + 's';

    var useSmall = cfg.size <= 160;
    var picture = document.createElement('picture');
    var webpSrc = useSmall ? cfg.webpSmall : cfg.webp;
    if (webpSrc) {
      var source = document.createElement('source');
      source.type = 'image/webp';
      source.srcset = webpSrc;
      picture.appendChild(source);
    }

    var img = document.createElement('img');
    img.src = useSmall ? cfg.srcSmall : cfg.src;   // PNG fallback
    img.alt = '';
    img.decoding = 'async';
    img.loading = 'lazy';
    img.width = cfg.size;
    img.height = Math.round(cfg.size / cfg.ratio); // reserva el alto: sin saltos
    picture.appendChild(img);
    wrap.appendChild(picture);
    return wrap;
  }

  // Libelula posada: se dibuja, pero no se mueve. Para quien ha pedido reducir
  // movimiento (si la duena eligio "quieta") y para equipos flojos.
  function buildStill(host) {
    if (host.dataset.milyReady === 'true') return;
    var cfg = readConfig(host);
    if (!cfg || !cfg.src) return;

    host.style.setProperty('--mily-df-opacity', cfg.opacity);
    host.classList.add('mily-dragonflies--still');

    var w = host.offsetWidth || window.innerWidth;
    var h = host.offsetHeight || 400;

    for (var i = 0; i < cfg.count; i++) {
      var wrap = createSprite(cfg, i);
      var frac = (i + 1) / (cfg.count + 1);
      var x = Math.max(0, (w - cfg.size) * frac);
      var y = h * (0.20 + (i % 2) * 0.34);
      var rot = (i % 2) ? 14 : -12;
      wrap.style.transform = 'translate3d(' + x.toFixed(0) + 'px,' + y.toFixed(0) + 'px,0) rotate(' + rot + 'deg)';
      host.appendChild(wrap);
    }
    host.dataset.milyReady = 'true';
  }

  function rand(min, max) { return min + Math.random() * (max - min); }

  function Flight(el, host, opts) {
    this.el = el;
    this.host = host;
    this.opts = opts;
    this.anim = null;
  }

  Flight.prototype.keyframes = function () {
    var w = this.host.offsetWidth || window.innerWidth;
    var h = this.host.offsetHeight || 400;
    var size = this.el.offsetWidth || 120;

    // Fly left-to-right or right-to-left, wandering vertically.
    var ltr = Math.random() > 0.5;
    var startX = ltr ? -size * 1.4 : w + size * 0.4;
    var endX   = ltr ? w + size * 0.4 : -size * 1.4;

    var band = this.opts.band; // 0..1 vertical lane
    var baseY = h * band;
    var drift = h * 0.14;
    var phase = this.opts.phase;
    var spanX = endX - startX;

    var frames = [];
    var steps = 16;
    var previous = null;

    for (var i = 0; i <= steps; i++) {
      var t = i / steps;
      var x = startX + spanX * t;
      var y = baseY + Math.sin(t * Math.PI * 2 + phase) * drift;

      // Point the sprite along its own path. The artwork is drawn head-up,
      // so the heading is the tangent of the curve turned a quarter turn.
      //   dx/dt = spanX                      (constant)
      //   dy/dt = drift * 2PI * cos(...)     (derivative of the sine)
      var dx = spanX;
      var dy = drift * Math.PI * 2 * Math.cos(t * Math.PI * 2 + phase);
      var heading = Math.atan2(dy, dx) * (180 / Math.PI) + 90;

      // atan2 wraps at +/-180 degrees. On a right-to-left flight dx is
      // negative, so the number flips between +180 and -180 twice per cycle
      // while the real turn is only a few degrees. The browser interpolates
      // that literally and the dragonfly spins all the way round its own
      // axis. Unwrap it: keep every step within half a turn of the last one,
      // so the number follows the real heading instead of jumping.
      if (previous !== null) {
        while (heading - previous > 180)  { heading -= 360; }
        while (heading - previous < -180) { heading += 360; }
      }
      previous = heading;

      var scale = 0.9 + Math.sin(t * Math.PI) * 0.16;

      frames.push({
        transform: 'translate3d(' + x.toFixed(1) + 'px,' + y.toFixed(1) + 'px,0) ' +
                   'rotate(' + heading.toFixed(1) + 'deg) ' +
                   'scale(' + scale.toFixed(3) + ')'
      });
    }
    return frames;
  };

  Flight.prototype.start = function () {
    if (this.anim) { this.anim.play(); return; }
    if (!this.el.animate) return;
    this.anim = this.el.animate(this.keyframes(), {
      duration: this.opts.duration,
      delay: this.opts.delay,
      iterations: Infinity,
      easing: 'linear'
    });
  };

  Flight.prototype.pause  = function () { if (this.anim) this.anim.pause(); };
  Flight.prototype.resume = function () { if (this.anim) this.anim.play();  };
  Flight.prototype.stop   = function () { if (this.anim) { this.anim.cancel(); this.anim = null; } };

  function build(host) {
    if (host.dataset.milyReady === 'true') return;

    var cfg = readConfig(host);
    if (!cfg || !cfg.src) return;

    var count = cfg.count;
    var speed = cfg.speed;

    host.classList.remove('mily-dragonflies--still');
    host.style.setProperty('--mily-df-opacity', cfg.opacity);

    var flights = [];
    for (var i = 0; i < count; i++) {
      var wrap = createSprite(cfg, i);
      host.appendChild(wrap);

      flights.push(new Flight(wrap, host, {
        band: 0.18 + (i / Math.max(count, 1)) * 0.5 + rand(-0.05, 0.05),
        phase: rand(0, Math.PI * 2),
        duration: rand(19000, 27000) / speed,
        delay: -rand(0, 12000)
      }));
    }

    host.dataset.milyReady = 'true';

    var visible = false;

    function sync() {
      var active = visible && document.visibilityState === 'visible';
      flights.forEach(function (f) { active ? f.start() : f.pause(); });
      // El aleteo vive en CSS, asi que la Web Animations API no lo alcanza:
      // se para por separado para no gastar bateria fuera de pantalla.
      host.dataset.milyPaused = active ? 'false' : 'true';
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
        sync();
      }, { rootMargin: '120px' }).observe(host);
    } else {
      visible = true;
    }

    document.addEventListener('visibilitychange', sync);

    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        flights.forEach(function (f) { f.stop(); });
        if (visible) sync();
      }, 250);
    });

    sync();
  }

  // Decide, capa por capa, que hacer. Tres cosas mandan aqui:
  //   1. Ahorro de datos: no se descarga nada.
  //   2. La preferencia del sistema de reducir movimiento, cruzada con lo que
  //      haya elegido la duena en el ajuste "Si el visitante pide reducir
  //      movimiento": volar igual / quieta / oculta.
  //   3. Equipos flojos: se dibuja, pero posada.
  function boot() {
    if (saveDataOn()) return;
    var reduced = !!(REDUCED && REDUCED.matches);

    document.querySelectorAll('.mily-dragonflies').forEach(function (host) {
      var mode = host.dataset.reduced || 'fly';
      if (reduced) {
        if (mode === 'hide') return;
        if (mode === 'static') { buildStill(host); return; }
      }
      if (deviceIsWeak()) { buildStill(host); return; }
      build(host);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-run when a section is re-rendered inside the theme editor.
  document.addEventListener('shopify:section:load', boot);

  // Si la visitante cambia la preferencia a mitad de sesion, se reconstruye
  // segun lo que corresponda ahora.
  if (REDUCED && REDUCED.addEventListener) {
    REDUCED.addEventListener('change', function () {
      document.querySelectorAll('.mily-dragonflies').forEach(function (h) {
        h.innerHTML = '';
        h.dataset.milyReady = 'false';
        h.classList.remove('mily-dragonflies--still');
      });
      boot();
    });
  }
})();
